import { DEFAULT_LOCALE, LOCALES } from '../domain/enums';
import { isValidIanaTimeZone } from '../domain/text-rules';
import { defaultBrand } from '../design/tokens';
import { en } from './locales/en';
import { tr } from './locales/tr';
export const messagesByLocale = {
    tr: tr,
    en,
};
export function isLocale(value) {
    return typeof value === 'string' && LOCALES.includes(value);
}
/** `Accept-Language` / cihaz dili → desteklenen dil. Bilinmiyorsa varsayılan. */
export function resolveLocale(candidate) {
    if (!candidate)
        return DEFAULT_LOCALE;
    const normalized = candidate.split(',')[0]?.split('-')[0]?.trim().toLowerCase();
    return isLocale(normalized) ? normalized : DEFAULT_LOCALE;
}
/**
 * Anahtar yolu çözümü.
 *
 * YAPRAK ANAHTAR NOKTA İÇEREBİLİR (Blok 30 · P1 turu): `validation` bölümünün
 * anahtarları Joi kural adlarıdır ve NOKTALIDIR (`validation['string.min']`).
 * Eski çözüm anahtarı düz `split('.')` ile yürüdüğü için o bölüm HİÇBİR ZAMAN
 * çözülemiyordu — 30 cümlelik `validation` sözlüğü ölü koddu ve alan bazlı
 * doğrulama hatası hiçbir ekranda yazılamıyordu. Bir seviyede parça
 * bulunamazsa KALAN yol tek bir anahtar olarak denenir; iç içe tanım her zaman
 * önceliklidir, yani mevcut anahtarların çözümü değişmez.
 */
function lookup(messages, key) {
    const parts = key.split('.');
    let current = messages;
    for (let index = 0; index < parts.length; index += 1) {
        if (typeof current !== 'object' || current === null)
            return undefined;
        const record = current;
        const part = parts[index];
        if (part in record) {
            current = record[part];
            continue;
        }
        const rest = parts.slice(index).join('.');
        if (!(rest in record))
            return undefined;
        current = record[rest];
        break;
    }
    return typeof current === 'string' ? current : undefined;
}
/**
 * ÇOĞUL: `count` parametresi VARSA önce `<anahtar>_one` / `<anahtar>_other`
 * denenir, yoksa düz anahtara düşülür.
 *
 * TR sayıdan sonra çoğul eki almaz (bu yüzden TR varyantları aynı cümledir),
 * EN alır: "1 times suggested" QA Blok 15'te bulundu. Tam ICU çoğul kuralı
 * (few/many) TR+EN için gereksizdir; iki biçim yeter.
 */
function pluralKey(key, params) {
    const count = params?.count;
    if (typeof count !== 'number' || !Number.isFinite(count))
        return null;
    return `${key}_${Math.abs(count) === 1 ? 'one' : 'other'}`;
}
function interpolate(template, params) {
    return template.replace(/\{(\w+)\}/g, (match, name) => {
        const value = params[name];
        return value === undefined ? match : String(value);
    });
}
const localeTag = { tr: 'tr-TR', en: 'en-US' };
/**
 * Göreli zaman birimleri BÜYÜKTEN KÜÇÜĞE; hiçbiri eşleşmezse "şimdi" yazılır.
 *
 * `Intl.RelativeTimeFormat` KULLANILMAZ: Hermes (React Native) onu SAĞLAMAZ ve
 * bildirim satırı "undefined cannot be used as a constructor" ile ÇÖKÜYORDU
 * (emülatörde ölçüldü). Metin sözlükten gelir; çoğul `count` ile çekimlenir ve
 * `plural-coverage` kapısı iki dili birden denetler.
 */
const RELATIVE_UNITS = [
    ['time.yearsAgo', 31536000],
    ['time.monthsAgo', 2592000],
    ['time.weeksAgo', 604800],
    ['time.daysAgo', 86400],
    ['time.hoursAgo', 3600],
    ['time.minutesAgo', 60],
];
export function createTranslator(locale, options = {}) {
    const brand = options.brand ?? defaultBrand;
    const messages = messagesByLocale[locale];
    const tag = localeTag[locale];
    // Marka adı ve domain hiçbir metne gömülmez; her çeviriye otomatik parametre olarak geçer.
    const brandParams = {
        brandName: brand.name,
        brandShortName: brand.shortName,
        appDomain: brand.domain,
        publicDomain: brand.publicDomain,
        supportEmail: brand.supportEmail,
    };
    return {
        locale,
        t(key, params) {
            const variant = pluralKey(key, params);
            const template = (variant ? lookup(messages, variant) : undefined) ??
                lookup(messages, key) ??
                (variant ? lookup(messagesByLocale[DEFAULT_LOCALE], variant) : undefined) ??
                lookup(messagesByLocale[DEFAULT_LOCALE], key);
            if (template === undefined) {
                options.onMissingKey?.(key, locale);
                if (options.strict) {
                    throw new Error(`Missing translation key: ${key} (${locale})`);
                }
                return '';
            }
            /*
             * `count` çoğulu SAYIYLA seçer, ama ekrana YEREL BİÇİMLE yazılır (T5 QA
             * P3-1): "1000 yer kaldı" değil "1.000 yer kaldı" / "1,000 spots left".
             * Tip `count`u sayıya kilitlediği için çağıran onu biçimleyemez; biçim
             * burada, tek yerde uygulanır. 1.000'in altındaki tam sayılarda çıktı aynıdır.
             */
            const count = params?.count;
            const shown = typeof count === 'number' && Number.isFinite(count)
                ? { count: new Intl.NumberFormat(tag).format(count) }
                : {};
            return interpolate(template, { ...brandParams, ...params, ...shown });
        },
        formatDate(value, dateOptions) {
            const date = typeof value === 'string' ? new Date(value) : value;
            return new Intl.DateTimeFormat(tag, dateOptions ?? { dateStyle: 'medium', timeStyle: 'short' }).format(date);
        },
        formatDateTimeInZone(value, timeZone) {
            const date = typeof value === 'string' ? new Date(value) : value;
            // Savunma hattı (QA P3): geçersiz IANA adı sayfa/e-posta render'ını ÇÖKERTMEZ,
            // UTC'ye düşülür. Şema doğrulaması geçersiz değeri zaten reddeder.
            const safeZone = isValidIanaTimeZone(timeZone) ? timeZone : 'UTC';
            // dateStyle/timeStyle, timeZoneName ile birlikte kullanılamaz — alanlar tek tek verilir.
            return new Intl.DateTimeFormat(tag, {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: safeZone,
                timeZoneName: 'short',
            }).format(date);
        },
        formatNumber(value, numberOptions) {
            return new Intl.NumberFormat(tag, numberOptions).format(value);
        },
        formatRelativeDays(days) {
            return new Intl.RelativeTimeFormat(tag, { numeric: 'auto' }).format(days, 'day');
        },
        formatRelativeTime(value, now) {
            const date = typeof value === 'string' ? new Date(value) : value;
            // `Date.now()` — `new Date()` yasak (zaman kaynağı enjekte edilir kuralı);
            // `now` verilmezse sistem saati okunur, testler damgayı geçirir.
            const seconds = Math.round(((now?.getTime() ?? Date.now()) - date.getTime()) / 1000);
            for (const [key, size] of RELATIVE_UNITS) {
                const count = Math.trunc(Math.abs(seconds) / size);
                if (count >= 1)
                    return this.t(key, { count });
            }
            return this.t('time.now');
        },
    };
}
//# sourceMappingURL=translator.js.map